import { z } from 'zod';
import { flowModeSchema, runStatusSchema } from '../status/run.js';
import { eventSeveritySchema } from '../status/severity.js';
import { isoDateTimeSchema, subjectRefSchema } from './common.js';
/**
 * Tableau de bord (`02` §2) après la refonte du 10/09/2026 : trois lignes de
 * tuiles **globales** — Activité, À traiter, Santé — au lieu d'une carte par
 * boutique. La répartition par origine reste disponible en infobulle sur
 * chaque tuile d'activité.
 */
/** Un compteur global plus sa répartition par origine (clé = code boutique ou `pennylane`). */
export const dashboardMetricSchema = z.object({
    total: z.number().int().min(0),
    byOrigin: z.record(z.string(), z.number().int().min(0)),
});
export const dashboardActivityWindowSchema = z.object({
    received: dashboardMetricSchema,
    transmitted: dashboardMetricSchema,
    shipped: dashboardMetricSchema,
});
/** Ligne « Activité » : aujourd'hui et les 7 derniers jours. */
export const dashboardActivitySchema = z.object({
    today: dashboardActivityWindowSchema,
    last7Days: dashboardActivityWindowSchema,
    selected: dashboardActivityWindowSchema.optional(),
});
/**
 * Ligne « À traiter » : chaque compteur mène à une liste filtrée. `blocked`
 * regroupe tout ce qui attend une action sur la liste Commandes : commandes en
 * erreur ou bloquées, factures Pennylane en erreur.
 */
export const dashboardTodoSchema = z.object({
    blocked: z.number().int().min(0),
    fulfillmentLineErrors: z.number().int().min(0),
});
/** Santé des webhooks : la boutique FR, plus la pire des autres. */
export const dashboardWebhookHealthSchema = z.object({
    store: z.string(),
    name: z.string(),
    lastWebhookAt: isoDateTimeSchema.nullable(),
    /** `true` quand le silence dépasse le seuil configuré pour cette boutique. */
    silent: z.boolean(),
});
export const dashboardRunHealthSchema = z.object({
    mode: flowModeSchema,
    enabled: z.boolean(),
    lastRunAt: isoDateTimeSchema.nullable(),
    lastRunStatus: runStatusSchema.nullable(),
    nextRunAt: isoDateTimeSchema.nullable(),
    runInProgress: z.boolean(),
});
export const dashboardWorkerHealthSchema = z.object({
    heartbeatAt: isoDateTimeSchema.nullable(),
    stale: z.boolean(),
    version: z.string(),
});
/** Ligne « Santé ». `webhooks` liste FR puis, s'il y en a une, la pire des autres. */
export const dashboardHealthSchema = z.object({
    webhooks: z.array(dashboardWebhookHealthSchema),
    fulfillment: dashboardRunHealthSchema,
    pennylane: dashboardRunHealthSchema,
    worker: dashboardWorkerHealthSchema,
    overall: z.enum(['ok', 'degraded', 'unknown']).optional(),
    integrations: z.array(z.object({
        key: z.string(),
        label: z.string(),
        status: z.enum(['ok', 'degraded', 'unknown', 'inactive']),
        checkedAt: isoDateTimeSchema.nullable(),
        lastActivityAt: isoDateTimeSchema.nullable(),
        message: z.string().nullable(),
    })).optional(),
});
/** Alerte des dernières 24 h (`02` §2). */
export const dashboardAlertSchema = z.object({
    id: z.string(),
    at: isoDateTimeSchema,
    severity: eventSeveritySchema,
    titleFr: z.string(),
    message: z.string(),
    origin: z.string().nullable(),
    subject: subjectRefSchema.nullable(),
});
export const dashboardAlertGroupSchema = z.object({
    code: z.string(),
    /** `critical` dès qu'une occurrence l'est (rouge), sinon `warning` (orange). */
    severity: eventSeveritySchema,
    titleFr: z.string(),
    count: z.number().int().min(0),
    lastAt: isoDateTimeSchema,
    /**
     * Toutes les occurrences visent une commande ou une facture connue : le
     * lien mène alors à la liste Commandes filtrée plutôt qu'au Journal.
     */
    ordersListable: z.boolean().optional(),
});
/** Bandeau « Envois vers Martelet suspendus » (`02` §2). */
export const dashboardMaintenanceSchema = z.object({
    enabled: z.boolean(),
    since: isoDateTimeSchema.nullable(),
    reason: z.string().nullable(),
});
export const dashboardPayloadSchema = z.object({
    maintenance: dashboardMaintenanceSchema,
    activity: dashboardActivitySchema,
    period: z.object({ from: isoDateTimeSchema, to: isoDateTimeSchema }).optional(),
    todo: dashboardTodoSchema,
    health: dashboardHealthSchema,
    alerts: z.array(dashboardAlertSchema),
    alertGroups: z.array(dashboardAlertGroupSchema).optional(),
});
//# sourceMappingURL=dashboard.js.map